import { useCallback, useEffect, useRef, useState } from 'react';
import type { NexusIntegrationFlow } from '../components/steps/IntegrationStep/Nexus/types';

type SetStateAction<T> = T | ((value: T) => T);

export enum LocalStorageErrorType {
  DISABLED = 'DISABLED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  UNKNOWN = 'UNKNOWN',
}

export interface LocalStorageError {
  type: LocalStorageErrorType;
  message: string;
}

const isBrowser = () => typeof window !== 'undefined';

const isLocalStorageAvailable = (): boolean => {
  if (!isBrowser()) return false;
  try {
    const testKey = '__localStorage_test__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

export const useLocalStorage = <T,>(key: string, initialValue: T) => {
  const [error, setError] = useState<LocalStorageError | null>(null);

  const initializer = useCallback((): T => {
    if (!isBrowser()) return initialValue;

    // Check if localStorage is available
    if (!isLocalStorageAvailable()) {
      setError({
        type: LocalStorageErrorType.DISABLED,
        message: 'Browser storage is disabled. Please enable cookies and site data in your browser settings.',
      });
      return initialValue;
    }

    try {
      const value = window.localStorage.getItem(key);
      if (value === null) return initialValue;
      return JSON.parse(value) as T;
    } catch {
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState<T>(initializer);
  const keyRef = useRef(key);
  keyRef.current = key;

  const setValue = useCallback(
    (value: SetStateAction<T>) => {
      setStoredValue((prev) => {
        const valueToStore = value instanceof Function ? value(prev) : value;

        if (isBrowser()) {
          try {
            window.localStorage.setItem(keyRef.current, JSON.stringify(valueToStore));
            // Clear error on successful write
            setError(null);
          } catch (err) {
            // Detect specific error types
            if (err instanceof DOMException) {
              if (err.name === 'QuotaExceededError') {
                setError({
                  type: LocalStorageErrorType.QUOTA_EXCEEDED,
                  message: 'Browser storage is full. Please clear some browser data to continue.',
                });
              } else if (err.name === 'SecurityError') {
                setError({
                  type: LocalStorageErrorType.DISABLED,
                  message: 'Browser storage is disabled. Please enable cookies and site data in your browser settings.',
                });
              } else {
                setError({
                  type: LocalStorageErrorType.UNKNOWN,
                  message: 'Failed to save data to browser storage.',
                });
              }
            } else {
              setError({
                type: LocalStorageErrorType.UNKNOWN,
                message: 'Failed to save data to browser storage.',
              });
            }
          }
        }

        return valueToStore;
      });
    },
    [setStoredValue]
  );

  useEffect(() => {
    if (!isBrowser()) return;
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== keyRef.current) return;
      try {
        setStoredValue(event.newValue ? (JSON.parse(event.newValue) as T) : initialValue);
      } catch {
        setStoredValue(initialValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [initialValue]);

  return [storedValue, setValue, error] as const;
};

export default useLocalStorage;

export const REGULAR_NEXUS_THREAD = 'regular';

export type NexusThreadFlow = NexusIntegrationFlow | typeof REGULAR_NEXUS_THREAD;

const NEXUS_THREAD_ID_KEY = 'nexus_thread_id';
const NEXUS_THREAD_FLOW_KEY = 'nexus_thread_flow';

/**
 * Persists the Nexus thread_id, and the flow that started it, in localStorage.
 * @returns [threadId, setThreadId, error, threadFlow] tuple
 */
export function useNexusThreadId() {
  const [threadId, setStoredThreadId, error] = useLocalStorage<string | null>(NEXUS_THREAD_ID_KEY, null);
  const [threadFlow, setStoredThreadFlow] = useLocalStorage<NexusThreadFlow | null>(NEXUS_THREAD_FLOW_KEY, null);

  const setThreadId = useCallback(
    (nextThreadId: string | null, flow?: NexusThreadFlow) => {
      setStoredThreadId(nextThreadId);
      setStoredThreadFlow(nextThreadId === null ? null : flow ?? null);
    },
    [setStoredThreadId, setStoredThreadFlow]
  );

  return [threadId, setThreadId, error, threadFlow] as const;
}
